/**
 * meihua.js — 梅花易数
 *
 * 与六爻纳甲是两套独立体系，不要混用：
 *   六爻纳甲 —— 装干支、配六亲、以用神生克断事，重「谁、什么、如何」
 *   梅花易数 —— 只看体用两卦的五行生克，重「成与不成、快与慢」
 *
 * 梅花的严谨性在于起卦法必须守规矩：
 *   - 时间起卦用**农历**年月日时，年取地支序数（子1…亥12），不是公元年数
 *   - 卦数用**先天八卦数**：乾1 兑2 离3 震4 巽5 坎6 艮7 坤8
 *   - 动爻取六除，上下卦取八除，余零则取满数
 *   - 动爻所在的经卦为「用」，另一卦为「体」——体只有一个，不会两边都是
 */

import { toLunar, hourBranchOrdinal, branchOrdinal } from './lunar.js';
import { fourPillars } from './ganzhi.js';
import { buildHexagram, TRIGRAM_BITS, TRIGRAM_NATURE, HEXAGRAM_NAMES } from './hexagram.js';

/** 先天八卦数 */
export const XIANTIAN_NUMBER = { 乾: 1, 兑: 2, 离: 3, 震: 4, 巽: 5, 坎: 6, 艮: 7, 坤: 8 };
export const NUMBER_TRIGRAM = { 1: '乾', 2: '兑', 3: '离', 4: '震', 5: '巽', 6: '坎', 7: '艮', 8: '坤' };

/** 八卦五行 */
export const TRIGRAM_ELEMENT = {
  乾: '金', 兑: '金', 震: '木', 巽: '木', 坤: '土', 艮: '土', 离: '火', 坎: '水'
};

/** 后天八卦方位 */
export const DIRECTION_TRIGRAM = {
  北: '坎', 东北: '艮', 东: '震', 东南: '巽',
  南: '离', 西南: '坤', 西: '兑', 西北: '乾'
};

/** 万物类象（节选，够断日常事） */
export const TRIGRAM_IMAGE = {
  乾: { 人: '父、老人、官长、名人', 事: '决断、刚健、开创', 物: '金玉、圆物、车马', 身: '头、肺', 时: '秋' },
  坤: { 人: '母、老妇、众人、农人', 事: '柔顺、含容、迟缓', 物: '布帛、方物、土地', 身: '腹、脾', 时: '夏秋之交' },
  震: { 人: '长男、动者、警官', 事: '起动、惊恐、虚惊', 物: '木器、乐器、竹苇', 身: '足、肝', 时: '春' },
  巽: { 人: '长女、秀士、僧道', 事: '进退不果、往来、传闻', 物: '木香、绳直、风', 身: '股、胆', 时: '春夏之交' },
  坎: { 人: '中男、舟人、盗贼', 事: '险陷、隐伏、劳苦', 物: '水、酒、带核之物', 身: '耳、肾', 时: '冬' },
  离: { 人: '中女、文人、目疾者', 事: '文书、明察、争讼', 物: '火、灯、干燥物', 身: '目、心', 时: '夏' },
  艮: { 人: '少男、童子、山中人', 事: '阻隔、静止、守成', 物: '土石、瓜果、门阙', 身: '手指、背', 时: '冬春之交' },
  兑: { 人: '少女、妾、歌者、译者', 事: '口舌、喜悦、毁折', 物: '金刃、乐器、泽物', 身: '口、肺', 时: '秋' }
};

const SHENG = { 木: '火', 火: '土', 土: '金', 金: '水', 水: '木' };
const KE = { 木: '土', 土: '水', 水: '火', 火: '金', 金: '木' };

/** 余零取满数 */
const mod = (n, m) => ((n % m) + m) % m || m;

/* ------------------------------------------------------- 起卦 */

/**
 * 时间起卦（农历）。
 *   上卦 =（年支序 + 月 + 日）÷ 8 取余
 *   下卦 =（年支序 + 月 + 日 + 时序）÷ 8 取余
 *   动爻 =（年支序 + 月 + 日 + 时序）÷ 6 取余
 */
export function castByTime(at, opts = {}) {
  const lunar = toLunar(at.year, at.month, at.day);
  const pillars = fourPillars(at, opts);
  const yearOrd = branchOrdinal(pillars.year.branch);
  const hourOrd = hourBranchOrdinal(at.hour ?? 12);
  const sumUpper = yearOrd + lunar.month + lunar.day;
  const sumAll = sumUpper + hourOrd;

  return finish({
    upper: NUMBER_TRIGRAM[mod(sumUpper, 8)],
    lower: NUMBER_TRIGRAM[mod(sumAll, 8)],
    movingLine: mod(sumAll, 6),
    method: '时间起卦',
    detail: `农历${lunar.year}年${lunar.monthName}月${lunar.dayName}，${pillars.year.branch}年序${yearOrd}，时序${hourOrd}`,
    lunar,
    pillars
  });
}

/**
 * 数字起卦（两数法，也用于声音次数、字数、物数）。
 *   前数为上卦，后数为下卦，两数与时序之和取动爻。
 */
export function castByNumbers(a, b, at, opts = {}) {
  const hourOrd = at ? hourBranchOrdinal(at.hour ?? 12) : 0;
  const pillars = at ? fourPillars(at, opts) : null;
  return finish({
    upper: NUMBER_TRIGRAM[mod(a, 8)],
    lower: NUMBER_TRIGRAM[mod(b, 8)],
    movingLine: mod(a + b + hourOrd, 6),
    method: '数字起卦',
    detail: `上数 ${a}，下数 ${b}${hourOrd ? `，时序 ${hourOrd}` : ''}`,
    pillars
  });
}

/**
 * 方位起卦（端法后天起卦）。
 *   以所见物数为上卦，以来人／物所在方位之卦为下卦，合数加时序取动爻。
 *   方位取后天八卦方位，方位之数取该卦的先天数。
 */
export function castByDirection(objectCount, direction, at, opts = {}) {
  const trigram = DIRECTION_TRIGRAM[direction];
  if (!trigram) throw new Error(`方位须为 北/东北/东/东南/南/西南/西/西北，收到 ${direction}`);
  const dirNum = XIANTIAN_NUMBER[trigram];
  const hourOrd = at ? hourBranchOrdinal(at.hour ?? 12) : 0;
  const pillars = at ? fourPillars(at, opts) : null;
  return finish({
    upper: NUMBER_TRIGRAM[mod(objectCount, 8)],
    lower: trigram,
    movingLine: mod(objectCount + dirNum + hourOrd, 6),
    method: '方位起卦',
    detail: `物数 ${objectCount}，${direction}方属${trigram}（先天数 ${dirNum}）${hourOrd ? `，时序 ${hourOrd}` : ''}`,
    pillars
  });
}

/**
 * 直接给三个数起卦，用于复核古例或实现其他起卦法。
 *   upperSum ÷ 8 取上卦，lowerSum ÷ 8 取下卦，movingSum ÷ 6 取动爻。
 */
export function castByRaw({ upperSum, lowerSum, movingSum, note = '' }) {
  return finish({
    upper: NUMBER_TRIGRAM[mod(upperSum, 8)],
    lower: NUMBER_TRIGRAM[mod(lowerSum, 8)],
    movingLine: mod(movingSum, 6),
    method: '按数起卦',
    detail: note || `上 ${upperSum}，下 ${lowerSum}，动 ${movingSum}`
  });
}

/* --------------------------------------------------- 装卦与断卦 */

function trigramLines(name) {
  const b = TRIGRAM_BITS[name];
  return [b & 1, (b >> 1) & 1, (b >> 2) & 1];
}

function finish({ upper, lower, movingLine, method, detail, lunar, pillars }) {
  const lines = [...trigramLines(lower), ...trigramLines(upper)];
  const primary = buildHexagram(lines);

  // 变卦：动爻变
  const changedLines = [...lines];
  changedLines[movingLine - 1] ^= 1;
  const changed = buildHexagram(changedLines);

  // 互卦：下互取二三四爻，上互取三四五爻
  const huLines = [lines[1], lines[2], lines[3], lines[2], lines[3], lines[4]];
  const mutual = buildHexagram(huLines);

  // 体用：动爻在下卦(1–3)则下卦为用、上卦为体；反之亦然
  const movingInLower = movingLine <= 3;
  const yong = movingInLower ? lower : upper;
  const ti = movingInLower ? upper : lower;

  const tiEl = TRIGRAM_ELEMENT[ti];
  const yongEl = TRIGRAM_ELEMENT[yong];

  const relate = (otherEl, label) => {
    if (otherEl === tiEl) return { label, relation: '比和', judgement: '吉', note: `${label}与体同气` };
    if (SHENG[otherEl] === tiEl) return { label, relation: '生体', judgement: '大吉', note: `${label}生体，有助力` };
    if (SHENG[tiEl] === otherEl) return { label, relation: '体生', judgement: '小凶', note: `体生${label}，耗力` };
    if (KE[otherEl] === tiEl) return { label, relation: '克体', judgement: '大凶', note: `${label}克体，有阻` };
    if (KE[tiEl] === otherEl) return { label, relation: '体克', judgement: '小吉', note: `体克${label}，费力可成` };
    return { label, relation: '无涉', judgement: '平', note: '' };
  };

  // 互卦分上下互，各自与体卦论生克；变卦取变出的那一经卦
  const huLower = mutual.lower;
  const huUpper = mutual.upper;
  const changedTrigram = movingInLower ? changed.lower : changed.upper;

  const relations = [
    relate(yongEl, '用卦'),
    relate(TRIGRAM_ELEMENT[huLower], '下互'),
    relate(TRIGRAM_ELEMENT[huUpper], '上互'),
    relate(TRIGRAM_ELEMENT[changedTrigram], '变卦')
  ];

  // 体卦旺衰：得时则旺
  const season = pillars ? seasonOf(pillars.month.branch) : null;
  const tiState = season ? trigramSeasonState(ti, season) : null;

  const SCORE = { 大吉: 2, 小吉: 1, 吉: 1, 平: 0, 小凶: -1, 大凶: -2 };
  const total =
    SCORE[relations[0].judgement] * 2 + // 用卦权重最高
    SCORE[relations[1].judgement] * 0.7 +
    SCORE[relations[2].judgement] * 0.7 +
    SCORE[relations[3].judgement] * 1.3 +
    (tiState === '旺' ? 1 : tiState === '相' ? 0.5 : tiState === '死' ? -1 : tiState === '囚' ? -0.5 : 0);

  return {
    method,
    detail,
    lunar: lunar || null,
    pillars: pillars
      ? { 年: pillars.year.name, 月: pillars.month.name, 日: pillars.day.name, 时: pillars.hour.name }
      : null,
    upper,
    lower,
    movingLine,
    primary,
    mutual,
    changed,
    ti,
    yong,
    tiElement: tiEl,
    yongElement: yongEl,
    tiState,
    relations,
    score: Math.round(total * 100) / 100,
    images: { 体: TRIGRAM_IMAGE[ti], 用: TRIGRAM_IMAGE[yong] },
    facts: [
      `${method}：${detail}`,
      `本卦 ${primary.name}（上${upper}下${lower}），动${movingLine}爻`,
      `互卦 ${mutual.name}，变卦 ${changed.name}`,
      `体卦 ${ti}（${tiEl}）${tiState ? `，当令${tiState}` : ''}；用卦 ${yong}（${yongEl}）`,
      ...relations.map((r) => `${r.label} ${r.relation}${r.note ? `——${r.note}` : ''}（${r.judgement}）`)
    ]
  };
}

const SEASON_BY_BRANCH = {
  寅: '春', 卯: '春', 巳: '夏', 午: '夏', 申: '秋', 酉: '秋', 亥: '冬', 子: '冬',
  辰: '四季', 未: '四季', 戌: '四季', 丑: '四季'
};
const seasonOf = (branch) => SEASON_BY_BRANCH[branch];

const SEASON_ELEMENT = { 春: '木', 夏: '火', 秋: '金', 冬: '水', 四季: '土' };

function trigramSeasonState(trigram, season) {
  const el = TRIGRAM_ELEMENT[trigram];
  const s = SEASON_ELEMENT[season];
  if (el === s) return '旺';
  if (SHENG[s] === el) return '相';
  if (SHENG[el] === s) return '休';
  if (KE[el] === s) return '囚';
  if (KE[s] === el) return '死';
  return '平';
}

export { TRIGRAM_NATURE, HEXAGRAM_NAMES };
